import { PostHog } from 'posthog-node';
import * as Sentry from '@sentry/nextjs';
import { ExperimentVariant, experimentVariantMap } from '../context/flagging';
import { getProductIDFromShopifyGID, getProductMOQ } from './product';
import { ProductTag } from '../types/common';
import { getProductSalesQuantityByExternalProductId } from '../api/storefront/product';

export const createClient = () =>
  new PostHog(process.env.NEXT_PUBLIC_POSTHOG_STOREFRONT_KEY as string, {
    host: 'https://app.posthog.com',
  });

const getExperimentVariant = (flagStr: string, featureFlagKey: string): ExperimentVariant => {
  if (!(flagStr in experimentVariantMap)) {
    Sentry.captureException(new Error(`Invalid flag value for feature flag key ${featureFlagKey}`));
    return ExperimentVariant.Control;
  }
  return experimentVariantMap[flagStr];
};

const getExperimentFlag = async (client: PostHog, featureFlagKey: string, currentAnonID: string) => {
  let flag: ExperimentVariant = ExperimentVariant.Control;
  const isFlagEnabled = await client.isFeatureEnabled(featureFlagKey, currentAnonID);

  if (isFlagEnabled) {
    const flagStr = (await client.getFeatureFlag(featureFlagKey, currentAnonID)) as string;
    flag = getExperimentVariant(flagStr, featureFlagKey);
  }

  return flag;
};

const getFeatureFlag = async (client: PostHog, featureFlagKey: string, currentAnonID: string) =>
  client.isFeatureEnabled(featureFlagKey, currentAnonID);

/**
 * This function retrieves the boolean of a feature flag from PostHog using the flag key.
 * It is used in the server side only(Inside getServerSideProps).
 * If the feature flag does not exist/ error with the underlying processes, it defaults to false.
 *
 * @param {string} featureFlagKey
 * @param {GetServerSidePropsContext} ctx
 * @returns {Promise<ExperimentVariant>}
 */
export const retrieveFeatureFlag = async (featureFlagKey: string, currentAnonID: string, client: PostHog) => {
  let flag = false;
  try {
    flag = (await getFeatureFlag(client, featureFlagKey, currentAnonID)) || false;
  } catch (err) {
    Sentry.captureException(err);
  }
  return flag;
};

/**
 * This function retrieves the value of a feature flag from PostHog using the flag key.
 * It is used in the server side only(Inside getServerSideProps).
 * If the feature flag does not exist/ error with the underlying processes, it defaults to 'control'.
 *
 * @param {string} featureFlagKey
 * @param {GetServerSidePropsContext} ctx
 * @returns {Promise<ExperimentVariant>}
 */
export const retrieveExperimentalFlag = async (featureFlagKey: string, currentAnonID: string, client: PostHog) => {
  let flag: ExperimentVariant = ExperimentVariant.Control;
  try {
    flag = await getExperimentFlag(client, featureFlagKey, currentAnonID);
  } catch (err) {
    Sentry.captureException(err);
  }
  return flag;
};

export const isHideUnitsExperimentEligible = async (products: Shopify.Product[]) => {
  if (!products || products.length === 0) {
    return false;
  }

  const evaluations = await Promise.all(
    products.map(async (product) => {
      const hasLiveTag = product.tags.includes(ProductTag.Live);
      const hasPetitionTag = product.tags.includes(ProductTag.Petition);
      const hasEvergreenTag = product.tags.includes(ProductTag.Evergreen);
      const hasLimitedQuantityTag = product.tags.includes(ProductTag.Limited);
      const hasHideUnitsTag = product.tags.includes(ProductTag.HideUnits);
      const hasHideUnitsExemptTag = product.tags.includes(ProductTag.HideUnitsExempt);
      const moq = getProductMOQ(product);

      const unitsSold = await getProductSalesQuantityByExternalProductId(getProductIDFromShopifyGID(product.id)).then(
        (res) => {
          if (res && res.data) {
            return res.data.quantity;
          }
          return 0;
        },
      );

      const isEligible =
        hasLiveTag &&
        !hasPetitionTag &&
        !hasEvergreenTag &&
        !hasLimitedQuantityTag &&
        !hasHideUnitsTag &&
        !hasHideUnitsExemptTag &&
        unitsSold >= moq;

      return isEligible;
    }),
  );

  return evaluations.some((isEligible) => isEligible);
};
